import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Activity, Sparkles, Receipt, ShoppingBag, AlertTriangle, FileText, BookOpen, Banknote, X, Plane, Wallet } from 'lucide-react';
import api from '@/lib/api';

const ICON = {
  new_sale: ShoppingBag,
  new_expense: Receipt,
  expense_updated: Receipt,
  expense_deleted: Receipt,
  sale_deleted: ShoppingBag,
  match_confirmed: Sparkles,
  match_rejected: X,
  payslip_disputed: AlertTriangle,
  hr_doc_uploaded: FileText,
  journal_entry_created: BookOpen,
  leave_decided: Plane,
  expense_claim_decided: Wallet,
  payroll_run_completed: Banknote,
  default: Activity,
};

const TONE = {
  new_sale: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20',
  new_expense: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20',
  match_confirmed: 'text-violet-600 bg-violet-50 dark:bg-violet-900/20',
  match_rejected: 'text-rose-600 bg-rose-50 dark:bg-rose-900/20',
  payslip_disputed: 'text-rose-700 bg-rose-50 dark:bg-rose-900/20',
  hr_doc_uploaded: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
  journal_entry_created: 'text-cyan-700 bg-cyan-50 dark:bg-cyan-900/20',
  leave_decided: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20',
  expense_claim_decided: 'text-amber-700 bg-amber-50 dark:bg-amber-900/20',
  payroll_run_completed: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20',
  default: 'text-foreground bg-muted',
};

function formatEvent(e) {
  switch (e.type) {
    case 'new_sale':
      return { title: 'New sale', desc: `${Number(e.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · ${e.actor || 'system'}` };
    case 'new_expense':
      return { title: `Expense · ${e.category || ''}`.trim(), desc: `${Number(e.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · ${e.actor || 'system'}` };
    case 'match_confirmed': {
      const ent = e.entity ? `${e.entity.entity_type}: ${e.entity.name}` : (e.match_type || 'match');
      return { title: e.auto ? 'Auto-confirmed' : 'Match confirmed', desc: `${ent}${e.amount ? ` · ${Number(e.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}` };
    }
    case 'match_rejected':
      return { title: 'Match rejected', desc: `by ${e.actor || 'manager'}` };
    case 'payslip_disputed':
      return { title: 'Payslip disputed', desc: `${e.employee_name || 'employee'} · ${e.month || ''}` };
    case 'hr_doc_uploaded':
      return { title: 'HR document uploaded', desc: `${e.doc_type || ''} · ${e.actor || ''}`.trim() };
    case 'journal_entry_created':
      return { title: 'Journal entry', desc: `${e.reference || ''} · ${e.actor || 'system'}`.trim() };
    case 'leave_decided':
      return { title: `Leave ${e.decision}`, desc: `${e.employee_name || ''} · ${e.days_count || '?'}d ${e.leave_type || ''}`.trim() };
    case 'expense_claim_decided':
      return { title: `Expense claim ${e.decision}`, desc: `${e.employee_name || ''} · ${e.currency || ''} ${Number(e.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`.trim() };
    case 'payroll_run_completed':
      return { title: `Payroll · ${e.period || ''}`.trim(), desc: `${e.employee_count || 0} employees · net ${Number(e.total_net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
    default:
      return { title: e.type || 'event', desc: e.actor || '' };
  }
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

/**
 * Mini real-time activity stream — buffers WS events into a 10-item ring.
 * Reads from a global event bus (window.__wsBus__) populated by the WebSocket hook.
 * Hidden when no live events have ever arrived (avoids empty card on cold-start).
 */
export default function RealtimeActivityWidget() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const seenRef = useRef(new Set());

  // Seed from recent activity feed so the widget isn't empty on first render
  useEffect(() => {
    let cancelled = false;
    api.get('/me/activity?limit=10').then(r => {
      if (cancelled) return;
      const items = (r.data?.items || []).slice(0, 6).map(it => ({
        type: (it.action || '').replace(/-/g, '_'),
        actor: it.user_email,
        ts: it.timestamp,
        seed: true,
      }));
      setEvents(items);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Subscribe to the global ws bus (populated by useWebSocket)
    const handler = (event) => {
      const detail = event.detail;
      if (!detail || !detail.type) return;
      if (['hello', 'pong'].includes(detail.type)) {
        if (detail.type === 'hello') setConnected(true);
        return;
      }
      const id = `${detail.type}-${detail.ts}-${detail.match_id || detail.amount || ''}`;
      if (seenRef.current.has(id)) return;
      seenRef.current.add(id);
      setEvents(prev => [{ ...detail, _key: id }, ...prev].slice(0, 10));
    };
    window.addEventListener('ws:dashboard', handler);
    return () => window.removeEventListener('ws:dashboard', handler);
  }, []);

  if (events.length === 0) return null;

  return (
    <Card className="border-slate-200 dark:border-stone-800" data-testid="realtime-activity-widget">
      <CardContent className="pt-4 space-y-2 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
              <Activity size={14} className="text-slate-700 dark:text-slate-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold font-outfit flex items-center gap-2">
                Real-time stream
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${connected ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground'}`}></span>
                  {connected ? 'LIVE' : 'idle'}
                </span>
              </h3>
              <p className="text-[10px] text-muted-foreground">Last {events.length} events</p>
            </div>
          </div>
        </div>

        <ul className="space-y-1.5 max-h-[320px] overflow-y-auto" data-testid="realtime-stream-list">
          {events.map((e, i) => {
            const Icon = ICON[e.type] || ICON.default;
            const tone = TONE[e.type] || TONE.default;
            const fmt = formatEvent(e);
            return (
              <li key={e._key || `${e.type}-${i}`}
                className={`flex items-start gap-2 px-2 py-1.5 rounded-md text-xs ${e.seed ? 'opacity-60' : ''} ${i === 0 && !e.seed ? 'animate-pulse-once' : ''}`}
                data-testid={`stream-event-${i}`}>
                <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${tone}`}>
                  <Icon size={12} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{fmt.title}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{fmt.desc}</div>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0 font-mono">{formatTime(e.ts)}</span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
